<template>
   <div class='energy-base-line-bat' :class="{disabled: Boolean(panelTooltip)}" :title="panelTooltip">
      <process-header :step="3"/>
      <div class="body">
         <div class='step-title'>
            <span>Energy Baseline (BAT)</span>
            <div style="display: flex;">
               <add-button @click="addBATProject"> Add Cluster </add-button>
               <add-button icon='edit' @click="showCostParamForm=true">Edit Cost Rates</add-button>
            </div>
         </div>
         <el-table
            @cell-mouse-leave="clearBatHighlight"
            @cell-mouse-enter="highlighBatCluster"
            :data="tableData"
            :row-class-name="getRowClassNames"
            >
            <el-table-column label="Actions" fixed width="130">
               <template slot-scope="scope">
                  <el-button
                     @click="handleSelect(scope.row)"
                     size="mini"
                     icon="el-icon-tickets"
                     style="margin: 1px;"
                     circle
                     content="Show Results"
                     v-tippy
                  />
                  <el-button
                     @click="handleEdit(scope.$index, scope.row)"
                     size="mini"
                     icon="el-icon-edit"
                     style="margin: 1px;"
                     circle
                     content="Edit"
                     v-tippy
                  />
                  <el-button
                     @click="handleDelete(scope.$index, scope.row)"
                     size="mini"
                     style="margin: 1px;"
                     icon="el-icon-delete"
                     content="Delete"
                     v-tippy
                     circle
                  />
               </template>
            </el-table-column>
            <el-table-column
               v-for="{label, prop, width} in tableColumns"
               :key="prop"
               :prop="prop"
               :width="width"
               :label="label"
            />
         </el-table>
         <back-and-continue
            :back-route="{name: 'energy-baseline'}"
            :next-route="{name: 'energy-baseline-mhp'}"
         />
      </div>
      <common-dialog-form
         :visible="showBATForm"
         title="Cluster Information for BAT analysis"
         @close="showBATForm=false"
         @submit="handleFormSubmit"
         >
         <input-field
            label="Cluster name"
            v-model="bat_form.cluster_name"
            required
         />
         <input-field
            type="select"
            label="Tier Type"
            v-model="bat_form.tier_id"
            @input="setDefaultTierDemand"
            :options="tiers"
            required
         />
         <div v-if="bat_form.tier_id > 1">
            <label style="font-size: 0.9em;">Household Demand (Watt)</label>
            <el-input type="number" v-model="bat_form.hh_demand_W" :disabled="!bat_form.custom_hh_demand" :max="maxDemand" :min="minDemand">
               <el-button
                  @click="bat_form.custom_hh_demand = !bat_form.custom_hh_demand"
                  slot="append"
                  icon="el-icon-edit"
                  :content="'Click to edit household demand'"
                  v-tippy
                  >
               </el-button>
            </el-input>
         </div>
         <input-field
            type="select"
            label="SHS DC watt"
            v-model="bat_form.dc_watt"
            v-if="bat_form.tier_id==1 && bat_form.custom_hh_demand == false"
            :options="default_options.shs_dc"
         />
         <input-field
            type="radio"
            label="Input for household number"
            v-model="hh_input"
            :options="[
               {title: 'Calculate from OSM data', value: 'osm'},
               {title: 'Count from census', value: 'census'},
               {title: 'Manual', value: 'manual'},
            ]"
         />
         <input-field
            v-if="hh_input==='manual'"
            type="number"
            label="Household Number"
            v-model="bat_form.num_household"
            required
         />
         <input-field
            v-model="useOtherParameters"
            label="Use Other Parameters"
            type="checkbox"
            one-line
         />
         <el-collapse v-if="useOtherParameters">
            <el-collapse-item title="Other Parameters">
               <el-collapse accordion>
                  <el-collapse-item
                     v-for="group in categorizedLoadInfos"
                     :name="group.id"
                     :title="group.name"
                     :key="group.id"
                     >
                     <institution-load-input
                        v-for="(load_info,index) in group.load_infos"
                        :key="`${load_info.institution.id}`"
                        v-model="group.load_infos[index]"
                        :load-info="load_info"
                        />
                  </el-collapse-item>
               </el-collapse>
            </el-collapse-item>
         </el-collapse>
      </common-dialog-form>
      <common-dialog-form
         :visible="showCostParamForm"
         title="Edit bat cost calculation parameters"
         @close="showCostParamForm=false"
         @submit="handleCostParamSubmit"
         >
         <div v-if="cost_options">
            <el-collapse accordion>
               <div
                  v-for="([key,inputs]) in Object.entries(cost_options)"
                  :name="key"
                  :key="key"
                  >
                  <div style="margin: 10px 0;">
                     <input :id="key" type=checkbox v-model="use_default[key]">
                     <label style="margin-left: 5px;" :for="key">Use default {{ cost_labels[key] && cost_labels[key].toLowerCase() }} cost rate</label>
                     <br><small>Check the checkbox above to use the default cost rate (select category where required) or uncheck to show and modify the cost rates.</small>
                  </div>
                  <el-collapse-item
                     v-if="!use_default[key]"
                     :title="`${ cost_labels[key] }`"
                     >
                     <input-field
                        v-for="(input,index) in inputs.filter(i => !i.hidden)"
                        :key="`${ index }`"
                        :type="input.type"
                        v-model="input.value"
                        :label="input.label"
                        :extra-attributes="{step: 0.01}"
                     />
                  </el-collapse-item>
                  <input-field
                     type="select"
                     label="MHP Category"
                     v-if="key === 'mhp' && default_ids && use_default && use_default.mhp"
                     v-model="default_ids.mhp_cost_rate_id"
                     :options="default_options.mhp"
                     required
                  />
                  <input-field
                     type="select"
                     v-if="key === 'smg' && default_ids && use_default && use_default.smg"
                     label="SMG Category"
                     v-model="default_ids.smg_cost_rate_id"
                     :options="default_options.smg"
                     required
                  />
                  <hr>
               </div>
            </el-collapse>
         </div>
      </common-dialog-form>
   </div>
</template>

<script>
import ProcessHeader from "../Header";
import AddButton from '@/components/molecules/Home/Plan/AddButton';
import BackAndContinue from '@/components/molecules/Home/Plan/Back&Continue';
import CommonDialogForm from '@/components/organisms/Home/CommonDialogForm';
import InstitutionLoadInput from '@/components/molecules/Home/Plan/InstitutionLoadInput';
import InputField from '@/components/molecules/Login/InputField';

import { mapActions, mapGetters } from 'vuex';
import { Table,TableColumn,Button,Collapse,CollapseItem,Input, } from 'element-ui';

import { resetTools, selectPlanPolygonTool } from '../../../../../js/tools/bat';
import { bus } from '../../../../../main';
import { fetchWithAuthToken, postWithAuthToken } from '../../../../../axios';
import { hideLoading, showError, showLoading, showSuccess,confirm } from '../../../../../js/ui-helpers';
import { getLayerById,getStyle } from '../../../../../js/layers';
import { fitToExtent, getMap } from '../../../../../js/map';
import { getNavigator } from '../../../../../js/mapNavigator';

export default {
   name: "energy-baseline-bat",
   components: {
      ProcessHeader,
      CommonDialogForm,
      AddButton,
      InstitutionLoadInput,
      BackAndContinue,
      InputField,
      'el-table': Table,
      'el-table-column': TableColumn,
      'el-button': Button,
      'el-collapse': Collapse,
      'el-collapse-item': CollapseItem,
      'el-input': Input,
   },
   data()
   {
      const tableColumns = [
         {label: 'Id', prop: 'id', width: 80},
         {label: 'Cluster', prop: 'cluster_name'},
         {label: 'Settlement Name', prop: 'local_name', width: 200},
         {label: 'Household', prop: 'num_household', width: 150},
         {label: 'Total Demand (kW)', prop: 'total_demand_kW', width: 150},
      ];

      return {
         tableColumns,
         tableData: [],
         hh_input: 'osm',
         selectedBatId: null,
         useOtherParameters: false,
         bat_form: {
            tier_id: 3,
            plan_id: this.planId,
            custom_hh_demand: false,
            dc_watt: null,
            hh_demand_W: 0,
            cluster_name: '',
            num_household: null,
            polygon: null,
         },
         tiers: [],
         categorizedLoadInfos: [],
         showBATForm: false,
         panelTooltip: '',
         // cost calculation parameters data
         showCostParamForm: false,
         use_default: {
            'shs_ac': true,
            'mhp': true,
            'smg': true,
            'grid': true,
         },
         default_options: {
            mhp: [],
            smg: [],
            shs_dc: [],
         },
         default_ids: null,
         cost_options: {
            'shs_ac': [],
            'mhp': [],
            'smg': [],
            'grid': [],
         },
         cost_labels: {
         }
      };
   },
   computed: {
      ...mapGetters('plan', ['getPlan']),
      ...mapGetters('tools', ['getSstResults']),
      planId()
      {
         return this.$route.params.id;
      },
      minDemand() {
         const tier = this.getSelectedTierInfo();
         if (!tier) return 0;
         return tier.demand_W;
      },
      maxDemand() {
         const tier = this.getNextTierInfo();
         if (!tier) return 0;
         return tier.demand_W;
      },
   },
   created()
   {
      this.setBatResultsToShow(null);
      this.loadAllBatProjects();
      this.resetInsitutionForm();
      this.loadCostOptions();
      bus.$on('bat-cluster-drawend', (polygon) =>
      {
         this.showBATForm = true;
         this.bat_form.polygon = polygon;
         this.panelTooltip = ``;
      });
      bus.$on('bat-draw-canceled', () => this.panelTooltip=``);
   },
   mounted()
   {
      this.updatePlanLayerVisibilityFor({bat: true});
   },
   beforeDestroy()
   {
      this.setBatResultsToShow(null);
      resetTools();
   },
   methods: {
      ...mapActions('layers', ['updatePlanLayersFeatures', 'updatePlanLayerVisibilityFor']),
      ...mapActions('plan', ['setBatResultsToShow']),
      getRowClassNames({row})
      {
         if (row.id===this.selectedBatId)
            return 'selected-row';
         return '';
      },
      setDefaultTierDemand() {
         const tier = this.getSelectedTierInfo();
         if (tier) this.bat_form.hh_demand_W = tier.demand_W;
      },
      getSelectedTierInfo() {
         return this.tiers.find(tier => tier.value===this.bat_form.tier_id);
      },
      getNextTierInfo() {
         return this.tiers.find(tier => tier.value===this.bat_form.tier_id+1);
      },
      handleCostParamSubmit()
      {
         const data = {
            default_ids: this.default_ids,
            cost_params: this.getFormattedCostParams(),
            use_default: this.use_default,
         };
         postWithAuthToken(`/api/bat/energycosts/costrate/${this.planId}`, data)
         .then(() => showSuccess("Enery Cost Rate updated"))
         .catch(() => showError("Failed to update enery cost rate"))
         .finally(() => this.showCostParamForm = false);
      },
      async loadCostOptions()
      {
         await fetchWithAuthToken(`/api/bat/energycosts/costrate/${this.planId}`)
         .then(({data}) =>
         {
            this.cost_options = data.cost_options;
            this.default_ids = data.default_ids;
            this.use_default = data.use_default;
            this.default_options = data.default_options;
            this.tiers = data.default_options.tiers;
            this.cost_labels = data.labels;
            this.setDefaultTierDemand();
         })
         .catch(err => console.error(err));
      },
      highlighBatCluster(row)
      {
         const layer = getLayerById('plan_bat_clusters', getMap())
         layer.setStyle(feature  =>
         {
            const isSelected = feature.get('cluster_id')===row.id;
            return getStyle({
               geom: 'Polygon',
               fill:{ color:`rgba(0,240,240,${isSelected ? 0.5 : 0.1})` },
               stroke: {color: 'rgb(0,240,240)', width: isSelected ? 3 : 1.5},
               text: { text: `${feature.get('cluster_id')}`, },
            });
         });
      },
      clearBatHighlight()
      {
         const layer = getLayerById('plan_bat_clusters', getMap())
         layer.setStyle(feature  =>
         {
            return getStyle({
               geom: 'Polygon',
               fill:{ color:`rgba(0,240,240,0.1)` },
               stroke: {color: 'rgb(0,240,240)', width: 1.5},
               text: { text: `${feature.get('cluster_id')}`, },
            });
         });
      },
      resetInsitutionForm(batId)
      {
         fetchWithAuthToken( `api/bat/institutions/?bat_id=${batId || ''}` ).then( ({data}) =>
         {
            this.categorizedLoadInfos = data;
         });
      },
      addBATProject()
      {
         this.panelTooltip = `Bat cluster tool selected\nStart drawing in the map to add bat cluster\nPress Esc to quit\n`;
         selectPlanPolygonTool(getNavigator(), this.getPlan.municipality.sddmm);
         this.resetBatForm();
      },
      getLoadInfos() 
      {
         return Object.values(this.categorizedLoadInfos).reduce((acc,ctgry) => {
            return [...acc, ...ctgry.load_infos];
         }, []);
      },
      loadAllBatProjects()
      {
         fetchWithAuthToken(`api/bat/${this.planId}`)
         .then(({data}) =>
         {
            this.tableData = data;
         });
         this.setBatResultsToShow(null); // reset the results
         this.selectedBatId = null;
      },
      resetBatForm()
      {
         this.hh_input = 'osm';
         this.useOtherParameters = false;
         this.bat_form = {
            tier_id: 3,
            dc_watt: 50,
            plan_id: this.planId,
            custom_hh_demand: false,
            hh_demand_W: 0,
            cluster_name: '',
            num_household: null,
            polygon: null,
         };
         this.setDefaultTierDemand();
      },
      async selectProjectMunicipality(navigator)
      {
         const {sddmm} = this.getPlan.municipality;
         const scode = parseInt(sddmm.toString()[0]);
         const dcode = parseInt(sddmm.toString().slice(1,3));
         await navigator.state.select(scode);
         await navigator.district.select(dcode);
         await navigator.municipality.select(parseInt(sddmm))
      },
      handleSelect(row)
      {
         showLoading('loading bat project...');
         fetchWithAuthToken(`api/bat/zonal-query/?bat_id=${row.id}`)
         .then(({data}) =>
         {
            this.setBatResultsToShow(data);
            this.selectedBatId = row.id;
            data.extent && fitToExtent(data.extent);
            showSuccess(`BAT Cluster (${row.cluster_name}) Selected`);
         })
         .catch(() => showError(`BAT Cluster (${row.cluster_name}) Selection Failed`))
         .finally(hideLoading);
      },
      handleEdit(index, row)
      {
         this.panelTooltip = `Bat cluster tool selected\nStart drawing in the map to add bat cluster\nPress Esc to quit\n`;
         selectPlanPolygonTool(getNavigator(), this.getPlan.municipality.sddmm);
         this.resetBatForm();
         this.hh_input = 'manual';
         this.bat_form = {
            ...this.bat_form,
            id: row.id,
            tier_id: row.tier,
            plan_id: row.plan,
            cluster_name: row.cluster_name,
            num_household: row.num_household,
            custom_hh_demand: true,
            hh_demand_W: parseInt(row.per_hh_demand_W),
            polygon: null,
         };
         if (this.bat_form.tier_id===1)
         {
            this.bat_form.dc_watt = parseInt(row.per_hh_demand_W);
            this.bat_form.custom_hh_demand = false;
         }
         this.resetInsitutionForm(row.id);
         this.updatePlanLayersFeatures(this.planId);
         this.useOtherParameters = row.use_other_parameters;
      },
      handleDelete(index, row)
      {
         const deleteBATCluster = () =>
         {
            showLoading('Deleting...');
            postWithAuthToken('api/bat/zonal-query/', {'id': row.id}, {method: 'delete'})
            .then( () => {
               showSuccess("Energy Baseline(BAT)", `Successfully deleted bat cluster`);
               this.loadAllBatProjects();
               this.updatePlanLayersFeatures(this.planId);
            })
            .catch( () => showError("Energy Baseline(BAT)", `Failed deleting bat cluster`))
            .finally( () => hideLoading() );
         };
         confirm({
            title: `Delete BAT cluster?`,
            msg: `Are you sure you want to delete cluster: ${row.cluster_name}?`,
            onSuccess: deleteBATCluster,
         });
      },
      getFormattedCostParams()
      {
         const formatted = {};
         Object.entries(this.cost_options).forEach(([key, inputs]) =>
         {
            formatted[key] = inputs.reduce((acc, input) => {
               acc[input.name] = input.value;
               return acc;
            }, {});
         })
         return formatted;
      },
      async houseExistsInCluster()
      {
         try
         {
            showLoading('Checking if houses exists in the cluster');
            const res = await postWithAuthToken('api/bat/houses', {
               polygon: this.bat_form.polygon,
               hh_input: this.hh_input,
               num_household: this.bat_form.num_household
            });
            console.log(res.data);
            if (res.data.nHouses === 0) {
               return false;
            }
            return true;
         }
         catch (error)
         {
            console.error(error);
            showError("Some error occurred");
            return false;
         }
         finally
         {
            hideLoading();
         }
      },
      async handleFormSubmit()
      {
         if (!(await this.houseExistsInCluster()))
         {
            showError("No house in the given cluster");
            return 
         }
         this.showBATForm=false;
         const method = this.bat_form.id ? 'put' : 'post';
         showLoading('Processing...');
         postWithAuthToken('api/bat/zonal-query/', {
            bat_form: this.bat_form,
            polygon: this.bat_form.polygon,
            save_project: true,
            hh_input: this.hh_input,
            extra_params: this.useOtherParameters,
            load_info_data: this.getLoadInfos(),
         },{ method: method }, {raise404: true} )
         .then(() =>
         {
            this.loadAllBatProjects();
            this.updatePlanLayersFeatures(this.planId);
            hideLoading();
            showSuccess("Energy Baseline(BAT)", `Successfully ${method==='put' ? 'edited' : 'added'} a bat cluster`);
         })
         .catch(() =>
         {
            hideLoading();
            showError("Energy Baseline(BAT)", `Failed ${method==='put' ? 'editing' : 'adding'} a bat cluster`)
         })
         .finally(() =>
         {
            this.resetInsitutionForm();
         });
      },
   },
};
</script>

<style lang="scss">
.energy-base-line-bat {
   font-family: $primary-font;
   color: $font-color;
   &.disabled {
      cursor: not-allowed;
      * {
         pointer-events: none;
      }
   }
   > .body {
      padding: 0.6em;
   }
   .step-title {
      span {
         font-family: $secondary-font;
         font-weight: 500;
         font-size: 1.05em;
      }
      font-family: inherit;
      display: flex;
      justify-content: space-between;
   }
   .baseline-radio {
      margin-top: -0.9em;
      padding-left: 1em;
   }
   .note {
      font-size: 0.8em;
   }
   .el-table .selected-row {
      background: tint($primary-color, 85%);
   }
}
.el-input__suffix {
    right: -26px;
}
</style>
